// ThirdPartySystemId: Integer IDs for third-party systems (IPW, IPG, etc.)
// Stored in DB to save space (millions of transactions, few third-party systems)
// WARNING: Never change existing IDs, only add new ones

export const IpwSystemId = {
    GENERIC: 0,
    DEALERTRACK: 10001,
    PCPARTPICKER: 10002,
} as const;
export const IpwSystemIdKey = {
    GENERIC: 'GENERIC',
    DEALERTRACK: 'DEALERTRACK',
    PCPARTPICKER: 'PCPARTPICKER',
} as const;
export const IpwSystemIdKeyList = ['GENERIC', 'DEALERTRACK', 'PCPARTPICKER'] as const;
export type IpwSystemIdKey = (typeof IpwSystemIdKeyList)[number];

export type IpwSystemId = (typeof IpwSystemId)[keyof typeof IpwSystemId];

// Reverse lookup: ID -> name (for display purposes)
export const IpwSystemIdToName: Record<IpwSystemId, string> = {
    [IpwSystemId.GENERIC]: 'Generic',
    [IpwSystemId.DEALERTRACK]: 'Dealertrack',
    [IpwSystemId.PCPARTPICKER]: 'PCPartPicker',
} as const;

export const IpgSystemId = {
    VALOR_US: 1,
    CARDPOINTE: 2,
    CLOVER: 3,
} as const;
export const SupportedIpgSystemIds = [
    IpgSystemId.VALOR_US,
    IpgSystemId.CARDPOINTE,
    IpgSystemId.CLOVER,
] as const;
export const IpgSystemIdKey = {
    VALOR_US: 'VALOR_US',
    CARDPOINTE: 'CARDPOINTE',
    CLOVER: 'CLOVER',
} as const;
export const IpgSystemIdKeyList = ['VALOR_US', 'CARDPOINTE', 'CLOVER'] as const;
export type IpgSystemIdKey = (typeof IpgSystemIdKeyList)[number];

export type IpgSystemId = (typeof IpgSystemId)[keyof typeof IpgSystemId];
export type SupportedIpgSystemId = (typeof SupportedIpgSystemIds)[number];

// Reverse lookup: ID -> name (for display purposes)
export const IpgSystemIdToName: Record<IpgSystemId, string> = {
    [IpgSystemId.VALOR_US]: 'Valor US',
    [IpgSystemId.CARDPOINTE]: 'CardPointe',
    [IpgSystemId.CLOVER]: 'Clover',
} as const;

export const ThirdPartySystemId = {
    // Ipg Systems (1-9999)
    ...IpgSystemId,

    // Ipw Systems. GENERIC is the unspecified IPX system id.
    ...IpwSystemId,
} as const;

export type ThirdPartySystemId = (typeof ThirdPartySystemId)[keyof typeof ThirdPartySystemId];

// Reverse lookup: ID -> name (for display purposes)
export const ThirdPartySystemIdToName: Record<ThirdPartySystemId, string> = {
    [ThirdPartySystemId.GENERIC]: 'Generic',
    [ThirdPartySystemId.DEALERTRACK]: IpwSystemIdToName[IpwSystemId.DEALERTRACK],
    [ThirdPartySystemId.PCPARTPICKER]: IpwSystemIdToName[IpwSystemId.PCPARTPICKER],
    [ThirdPartySystemId.VALOR_US]: IpgSystemIdToName[IpgSystemId.VALOR_US],
    [ThirdPartySystemId.CARDPOINTE]: IpgSystemIdToName[IpgSystemId.CARDPOINTE],
    [ThirdPartySystemId.CLOVER]: IpgSystemIdToName[IpgSystemId.CLOVER],
} as const;

const SupportedThirdPartySystemIdSet: ReadonlySet<number> = new Set(
    Object.values(ThirdPartySystemId)
);

export function isThirdPartySystemId(systemId: number): systemId is ThirdPartySystemId {
    return SupportedThirdPartySystemIdSet.has(systemId);
}

export function assertThirdPartySystemId(systemId: number): asserts systemId is ThirdPartySystemId {
    if (!isThirdPartySystemId(systemId)) {
        throw new Error(`Unsupported third-party system id: ${systemId}`);
    }
}

export function formatThirdPartySystemName(systemId: number): string {
    assertThirdPartySystemId(systemId);
    return ThirdPartySystemIdToName[systemId];
}
